/**
 * bomImportMapping.js
 * Pure, framework-free module implementing the HU-13 BOM re-import mapping algorithm (S39-S44).
 * Extracted out of `Workflow.jsx#resetResources` (which becomes a thin consumer) so the full
 * contract can be unit-tested without mounting any component (same pattern as `bomTree.js`).
 *
 * GATE 1 (D2) decisions, not re-litigated here:
 * - The mapping lives entirely in the front-end; `WorkflowResource.equals` (backend) is untouched.
 * - Functional identity of a BOM component = ComponentKey (code + parent), never the raw row id.
 * - No match by code+parent => the component is treated as brand new (its previous job assignment
 *   is lost, `resourceQuantityBOM.deleted = true`, same behaviour as before this module existed).
 *   There is deliberately NO fallback to a code-only match (S44).
 * - A collision (several new candidates sharing the same code+parent) is resolved deterministically
 *   by the closest `position` (S43).
 * - When the mapping succeeds, the job assignment is preserved by re-pointing
 *   `resourceQuantityBOM.workflowResourceUUID` to the new component's uuid (S39).
 *
 * Key design decision (documented per Milestone 6 instructions): the "parent" half of the
 * ComponentKey is represented as the CHAIN OF ANCESTOR CODES from the root down to (but excluding)
 * the component itself — not the raw dotted `position` prefix. Both are derived from `position` to
 * build the hierarchy (there is no other structural link available), but only the ancestor chain
 * is compared:
 * - S41 requires that a component keeps mapping when it moves to a different `position` among its
 *   siblings (same code, same parent) — a raw position-prefix key would break here, since the
 *   component's own position changes; the ancestor-CODE-chain key does not, because it only
 *   depends on the parent's own code, never the child's own position.
 * - S42 requires that a component be treated as new when its parent changed position or was
 *   renamed between versions — the ancestor-code-chain key breaks here by construction, because
 *   the parent's own code (or its own ancestor chain, recursively) no longer matches.
 * A pure position-prefix key cannot satisfy both S41 and S42 at once (satisfying S41 requires
 * ignoring the child's own position, which a position-prefix key cannot do without also ignoring
 * a parent's position change). The ancestor-code-chain key satisfies both simultaneously.
 *
 * Since the ComponentKey is NOT unique (a BOM legitimately repeats the same code under the same
 * parent — the same screw twice), the matching inside a ComponentKey group is ONE-TO-ONE: every
 * imported candidate can be claimed by a single previous component, closest `position` first
 * (S43). Any previous component left without a free candidate has really disappeared from the new
 * BOM and is marked `deleted` (S44) — without the one-to-one constraint several previous
 * components collapsed onto the same new one and a removed component was never reported.
 *
 * @typedef {import('../../generic/utilities/objectStructures/workflow/workflow').WorkflowResource} WorkflowResource WorkflowResource object
 * @typedef {import('../../generic/utilities/objectStructures/workflow/workflow').Workflow} Workflow Workflow object
 * @typedef {import('../../generic/utilities/objectStructures/workflow/workflow').Node} Node Node object
 */

import { normalizeBomPosition } from './bomTree'

/**
 * Marks an ancestor that could not be resolved to a real row of the list (the parent position has
 * no component of its own). Keeping the unresolved parent position in the chain prevents an orphan
 * component from sharing its ComponentKey with a root component of the same code.
 */
const UNRESOLVED_PARENT_MARK = '?'

/**
 * Indexes a flat BOM component list by its own normalized `position`, so a child can resolve its
 * parent row from the dotted position prefix (the only structural link the BOM carries). When two
 * rows share the same normalized position (the CSV allows it), the first one wins — deterministic
 * and consistent with `bomTree.js#buildBomPositionTree`.
 * @param {WorkflowResource[]} items
 * @returns {Object<string, WorkflowResource>}
 */
const buildPositionIndex = (items) => {
  const positionIndex = {}
  ;(items || []).forEach((item) => {
    const normalizedPosition = normalizeBomPosition(item.position)
    if (!positionIndex[normalizedPosition]) {
      positionIndex[normalizedPosition] = item
    }
  })
  return positionIndex
}

/**
 * Resolves the chain of ancestor codes (root-first, excluding the item itself) of a position.
 * Both the item's position and the parent prefix are normalized before the lookup: a parent stored
 * as "390.00" is indexed as "390", so the raw prefix "390.00" of a child "390.00.10" would never
 * match it.
 * @param {string} position
 * @param {Object<string, WorkflowResource>} positionIndex
 * @param {Set<string>} visited Positions already walked, guarding against cyclic positions
 * @returns {string[]}
 */
const resolveAncestorCodes = (position, positionIndex, visited) => {
  const normalizedPosition = normalizeBomPosition(position)
  const lastDotIndex = normalizedPosition.lastIndexOf('.')
  if (lastDotIndex === -1 || visited.has(normalizedPosition)) {
    return []
  }

  const parentPosition = normalizeBomPosition(normalizedPosition.substring(0, lastDotIndex))
  const parentItem = positionIndex[parentPosition]
  if (!parentItem) {
    return [UNRESOLVED_PARENT_MARK + parentPosition]
  }

  const nextVisited = new Set(visited)
  nextVisited.add(normalizedPosition)
  return [...resolveAncestorCodes(parentItem.position, positionIndex, nextVisited), parentItem.resource?.code || '']
}

/**
 * Builds the ComponentKey string (code + parent, RN13.1/D2) from an own code and its ancestor code
 * chain.
 * @param {string} code
 * @param {string[]} ancestorCodes
 * @returns {string}
 */
const buildComponentKey = (code, ancestorCodes) => `${code}::${(ancestorCodes || []).join('>')}`

/**
 * Resolves, for every item of a flat BOM component list, its own ComponentKey. Keyed per ITEM (not
 * per position): two rows sharing the same `position` — which the BOM CSV allows — must each keep
 * their own code, otherwise one of them would be matched under the other's identity.
 * @param {WorkflowResource[]} items
 * @returns {{item: WorkflowResource, code: string, ancestorCodes: string[], key: string}[]}
 */
const buildComponentEntries = (items) => {
  const positionIndex = buildPositionIndex(items)

  return (items || []).map((item) => {
    const code = item.resource?.code || ''
    const ancestorCodes = resolveAncestorCodes(item.position, positionIndex, new Set())
    return { item, code, ancestorCodes, key: buildComponentKey(code, ancestorCodes) }
  })
}

/**
 * Groups component entries by their ComponentKey, preserving the list order inside each group.
 * @param {{key: string}[]} entries
 * @returns {Map<string, Object[]>}
 */
const groupEntriesByKey = (entries) => {
  const grouped = new Map()
  entries.forEach((entry) => {
    if (!grouped.has(entry.key)) {
      grouped.set(entry.key, [])
    }
    grouped.get(entry.key).push(entry)
  })
  return grouped
}

/**
 * S43 (D2): distance used to tie-break candidates sharing a ComponentKey — the numeric distance
 * between the two own `position` last segments (the ancestor chain is already known to match).
 * Non-numeric segments only "match" when the whole normalized position is identical.
 * @param {string} previousPosition
 * @param {string} candidatePosition
 * @returns {number}
 */
const positionDistance = (previousPosition, candidatePosition) => {
  const previousSegment = Number(normalizeBomPosition(previousPosition).split('.').pop())
  const candidateSegment = Number(normalizeBomPosition(candidatePosition).split('.').pop())
  if (Number.isNaN(previousSegment) || Number.isNaN(candidateSegment)) {
    return normalizeBomPosition(previousPosition) === normalizeBomPosition(candidatePosition)
      ? 0
      : Number.MAX_SAFE_INTEGER
  }
  return Math.abs(candidateSegment - previousSegment)
}

/**
 * S43 + one-to-one: matches the previous components of a ComponentKey group with the imported
 * candidates of the same group, closest `position` first, each candidate claimed at most once.
 * Deterministic: ties are broken by list order on both sides.
 * @param {Object[]} previousGroup
 * @param {Object[]} candidateGroup
 * @param {Object<string, WorkflowResource>} mapping Accumulator, keyed by previous uuid
 */
const matchGroupOneToOne = (previousGroup, candidateGroup, mapping) => {
  const pairs = []
  previousGroup.forEach((previousEntry, previousIndex) => {
    candidateGroup.forEach((candidateEntry, candidateIndex) => {
      pairs.push({
        previousIndex,
        candidateIndex,
        distance: positionDistance(previousEntry.item.position, candidateEntry.item.position)
      })
    })
  })

  pairs.sort((first, second) => {
    return first.distance - second.distance ||
      first.previousIndex - second.previousIndex ||
      first.candidateIndex - second.candidateIndex
  })

  const claimedPrevious = new Set()
  const claimedCandidates = new Set()
  pairs.forEach((pair) => {
    if (claimedPrevious.has(pair.previousIndex) || claimedCandidates.has(pair.candidateIndex)) {
      return
    }
    claimedPrevious.add(pair.previousIndex)
    claimedCandidates.add(pair.candidateIndex)
    mapping[previousGroup[pair.previousIndex].item.uuid] = candidateGroup[pair.candidateIndex].item
  })
}

/**
 * S39-S44 (HU-13/D2): resolves, for every component of the previous BOM, which component of the
 * newly imported BOM it maps to (or none). Keyed by the previous component's own `uuid`, since
 * that is exactly what a job's `resourceQuantityBOM.workflowResourceUUID` points to.
 * @param {WorkflowResource[]} previousItems Full flat list of the BOM before the import
 * @param {WorkflowResource[]} importedItems Full flat list of the newly imported BOM
 * @returns {Object<string, WorkflowResource>} Map of previous uuid -> matched imported component
 * (never present, i.e. `mapping[uuid]` is `undefined`, when there is no match — S42/S44)
 */
const resolveBomImportMapping = (previousItems, importedItems) => {
  const previousGroups = groupEntriesByKey(buildComponentEntries(previousItems))
  const importedGroups = groupEntriesByKey(buildComponentEntries(importedItems))

  const mapping = {}
  previousGroups.forEach((previousGroup, key) => {
    const candidateGroup = importedGroups.get(key) || []
    if (candidateGroup.length === 0) {
      // S42/S44: no match by code+parent -> treated as new, no fallback to a code-only match.
      return
    }
    matchGroupOneToOne(previousGroup, candidateGroup, mapping)
  })

  return mapping
}

/**
 * Re-links one job's resource list against the resolved mapping: BOM-backed resources are either
 * re-pointed to their new component (S39/S40/S41/S43) or marked `deleted` (S42/S44); resources
 * that carry no BOM component (dropped by hand onto the job) are left untouched.
 * @param {Object[]} resources
 * @param {Object<string, WorkflowResource>} mapping
 * @returns {Object[]}
 */
const remapJobResources = (resources, mapping) => {
  return (resources || []).map((resource) => {
    const workflowResourceUUID = resource.resourceQuantityBOM?.workflowResourceUUID
    if (!workflowResourceUUID) {
      return resource
    }

    const matched = mapping[workflowResourceUUID]
    if (!matched) {
      return {
        ...resource,
        resourceQuantityBOM: { ...resource.resourceQuantityBOM, deleted: true }
      }
    }

    return {
      ...resource,
      id: null,
      resourceQuantityBOM: {
        ...resource.resourceQuantityBOM,
        workflowResourceUUID: matched.uuid,
        engRevision: matched.engRevision,
        engItem: matched.engItem,
        quantity: matched.quantity,
        revision: matched.revision,
        position: matched.position,
        deleted: false
      }
    }
  })
}

/**
 * Walks every JOB of the workflow — including the ones nested inside subworkflows, exactly like
 * `bomTree.js#collectJobAssignments` does, since a BOM component can be consumed several levels
 * deep — re-linking their resources. Never mutates the input nodes.
 * @param {Node[]} nodes
 * @param {Object<string, WorkflowResource>} mapping
 * @returns {Node[]}
 */
const remapNodes = (nodes, mapping) => {
  return (nodes || []).map((node) => {
    if (node.nodeType === 'JOB') {
      return {
        ...node,
        jobReference: { ...node.jobReference, resources: remapJobResources(node.jobReference?.resources, mapping) }
      }
    }
    if (node.nodeType === 'WORKFLOW' && node.subWorkflow) {
      return {
        ...node,
        subWorkflow: { ...node.subWorkflow, nodes: remapNodes(node.subWorkflow.nodes, mapping) }
      }
    }
    return node
  })
}

/**
 * S39-S44 (HU-13): applies the BOM re-import mapping to a workflow's job resources, re-linking
 * each assigned component to its matched new component (preserving the JOB assignment, S39/S40/
 * S41/S43) or marking it `deleted` when there is no match (S42/S44) — never mutating the input
 * workflow.
 * @param {Workflow} workflow Current workflow (its `nodes[].jobReference.resources[]` carry the
 * job assignments)
 * @param {WorkflowResource[]} previousItems Full flat list of the BOM before the import
 * @param {WorkflowResource[]} importedItems Full flat list of the newly imported BOM
 * @returns {Workflow} A new workflow object with re-linked/deleted job resources
 */
const applyBomImportMapping = (workflow, previousItems, importedItems) => {
  const mapping = resolveBomImportMapping(previousItems, importedItems)

  return { ...workflow, nodes: remapNodes(workflow?.nodes, mapping) }
}

export {
  buildComponentEntries,
  buildComponentKey,
  resolveBomImportMapping,
  applyBomImportMapping
}
